import React, { ReactNode, useEffect, useRef } from 'react';
import styled from 'styled-components';

interface FilterListboxProps {
  readonly ariaLabel: string;
  readonly children: ReactNode;
  readonly className?: string;
  /** Adds `aria-multiselectable="true"` for listboxes where more than one option can be
      selected at once (schedule + landing category filters). Session pickers, where selecting
      commits and closes, leave this off. */
  readonly multiSelectable?: boolean;
}

const getFocusableOptions = (root: HTMLElement | null): HTMLButtonElement[] => {
  if (!root) return [];
  return Array.from(
    root.querySelectorAll<HTMLButtonElement>('[role="option"]:not(:disabled)'),
  );
};

/**
 * Menu surface for a filter popover — a `role="listbox"` container with padding, background,
 * radius, and a soft shadow. Renders inside the Popover atom's portal; the popover handles
 * positioning and open/close, this component only handles the menu's visual shell.
 * Options should be <FilterListboxOption> children.
 *
 * Keyboard model (WAI-ARIA listbox):
 *  - Arrow Up / Down: move focus to the previous / next enabled option (wraps at either end).
 *  - Home / End: jump to the first / last enabled option.
 *  - Enter / Space: activate the focused option (native `<button>` behaviour on the child).
 *  - On mount focus jumps to the currently selected option, or the first option if none is
 *    selected — so keyboard users have a target immediately after the popover opens.
 */
const FilterListbox = ({
  ariaLabel,
  children,
  className,
  multiSelectable,
}: FilterListboxProps) => {
  const surfaceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const options = getFocusableOptions(surfaceRef.current);
    if (options.length === 0) return;
    const selected = options.find(
      (option) => option.getAttribute('aria-selected') === 'true',
    );
    (selected ?? options[0]).focus({ preventScroll: true });
    // Mount-only: the popover unmounts on close so re-open re-runs this effect naturally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const options = getFocusableOptions(surfaceRef.current);
    if (options.length === 0) return;

    const currentIndex = options.findIndex(
      (option) => option === document.activeElement,
    );

    let nextIndex: number | null = null;
    switch (event.key) {
      case 'ArrowDown':
        nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % options.length;
        break;
      case 'ArrowUp':
        nextIndex =
          currentIndex < 0
            ? options.length - 1
            : (currentIndex - 1 + options.length) % options.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = options.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    options[nextIndex].focus();
  };

  return (
    <ListboxSurface
      aria-label={ariaLabel}
      aria-multiselectable={multiSelectable || undefined}
      className={className}
      onKeyDown={handleKeyDown}
      ref={surfaceRef}
      role="listbox"
    >
      {children}
    </ListboxSurface>
  );
};

export default FilterListbox;

const ListboxSurface = styled.div`
  background: var(--way-colors-coherentColorShades-100);
  border-radius: 8px;
  box-shadow: var(--way-design-boxShadow-l);
  display: flex;
  flex-direction: column;
  max-height: 320px;
  min-width: 180px;
  overflow-y: auto;
  padding: 6px 0;
`;
